// @flow
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import { setInputSelection } from '../../shared/dom.utils'
import HintWrapper from '../controls/HintWrapper'
import type { Options, Option } from '../types'
import ErrorText from '../controls/ErrorText'
import styled, { type StyledComponent } from 'styled-components'
import NativeSelect from '../controls/NativeSelect'

const currencyWidth = 15
const nativeSelectWidth = 85

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  display: table;
  .budget-select__input-item.input-item {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    margin-right: 0;
    border-right: none;
    padding-right: ${currencyWidth + 5}px;
    text-align: right;
    max-width: 100px;

    // hide arrows
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
  }
  .budget-select__hint {
    margin-left: 10px;
  }
`
const InputWrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  align-items: center;
`
const Currency: StyledComponent<Object, any, any> = styled.div`
  position: absolute;
  right: 0;
  bottom: 10px;
  width: ${currencyWidth}px;
  text-align: left;
`
const SelectWrapper: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  position: relative;
`
const StyledNativeSelect: any = styled(NativeSelect)`
  vertical-align: top;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`

type Props = {|
  budget: string,
  budgetCycle: string,
  cycleOptions: Options,
  maxBudget: number,
  minDailyBudget: number,
  minMonthBudget: number,
  autoFocus?: boolean,
  onChange?: Function,
  errors: Array<string>,
  hideHints?: boolean,
  isBudgetCycleDisabled?: boolean
|}

type State = {|
  errors: Array<string>
|}

export default class BudgetSelect extends Component<Props, State> {
  t: Function
  input: Object

  constructor(props: Props) {
    super(props)

    this.t = translateFunc(translations.budget_select)

    this.state = {
      errors: props.errors || []
    }
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      const length = this.input.value.length
      setInputSelection(this.input, length, length)
    }
  }

  setErrors(errors: Array<string>) {
    this.setState({ errors })
  }

  render() {
    return (
      <Wrapper>
        <InputWrapper>
          <SelectWrapper>
            <input
              value={isNaN(this.props.budget) ? '' : this.props.budget}
              onChange={(e) => this.budgetChanged(e)}
              className="budget-select__input-item input-item"
              id="audience_budget"
              type="text"
              autoFocus={this.props.autoFocus}
              ref={(el: any) => {
                this.input = el
              }}
            />
            <Currency>{this.t('currency')}</Currency>
          </SelectWrapper>
          <StyledNativeSelect
            width={nativeSelectWidth}
            onChange={(value) => this.cycleChanged(value)}
            value={this.props.budgetCycle}
            className="t-BudgetSelect__select"
            id="audience_budget_cycle"
            htmlProperties={{ disabled: this.props.isBudgetCycleDisabled }}
          >
            {this.props.cycleOptions.map((option, index) => this.renderOption(option, index))}
          </StyledNativeSelect>
          {!this.props.hideHints && (
            <HintWrapper
              triggerText={this.budgetRecommendation()}
              popupText={this.t('recommended_budget_hint')}
              className="budget-select__hint"
              alignLeft
              dashed
            />
          )}
        </InputWrapper>
        <ErrorText errors={this.state.errors} />
      </Wrapper>
    )
  }

  changed(options: { budget: string, budgetCycle: string }) {
    const { onChange } = this.props

    if (!onChange) return

    const { budget, errors } = this.validate(options.budget, options.budgetCycle)

    onChange && onChange({ budget, budgetCycle: options.budgetCycle, errors })
    this.setState({ errors })
  }

  budgetChanged(e: Object) {
    this.changed({ budget: e.target.value, budgetCycle: this.props.budgetCycle })
  }

  cycleChanged(value: string) {
    this.changed({ budget: this.props.budget, budgetCycle: value })
  }

  validate(budget?: string, cycle: string): { budget: string, errors: Array<string> } {
    let errors = []
    budget = (budget && budget.replace(/\D/, '')) || ''

    if (parseInt(budget) > this.props.maxBudget) {
      budget = this.props.budget
      errors = [this.t('maximum_value', { value: this.props.maxBudget })]
    } else if (budget === '') {
      errors = [this.t('cant_be_empty')]
    } else {
      let minBudget = cycle === 'day' ? this.props.minDailyBudget : this.props.minMonthBudget
      if (parseInt(budget) < minBudget) {
        errors = [this.t('minimum_value', { value: minBudget })]
      }
    }

    return { budget, errors }
  }

  budgetRecommendation() {
    return this.props.budgetCycle === 'day'
      ? this.t('recommended_daily_budget')
      : this.t('recommended_budget')
  }

  renderOption(option: Option, index: number) {
    let label = option[0]
    let value = option[1]

    return (
      <option key={index} value={value}>
        {label}
      </option>
    )
  }
}
